import { useConfirmation } from '@/contexts/confirmation'
import useCommitmentPriceTotal from '@/hooks/useCommitmentPriceTotal'
import useCommitmentTime from '@/hooks/useCommitmentTime'
import ConfigurationIcons from '@/icons/ConfigurationIcons'
import DeleteOutlineIcons from '@/icons/DeleteOutlineIcons'
import EyeIcons from '@/icons/EyeIcons'
import EyeOffIcons from '@/icons/EyeOffIcons'
import PricingIcons from '@/icons/PricingIcons'
import { PRICE_CONFIG } from '@/modules/Contract/constants'
import {
    Contract,
    SingleProductConfigOptionProps,
} from '@/modules/Contract/types'
import { FormField } from '@/components/ui/form-field'
import { useEffect, useState } from 'react'
import { Path, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { GridHookForm, IconButton, Typogaphy } from 'vnetwork-ui'
import PricingSection from '../shared/PricingSection'
import { BandwidthHelpDialog } from './bandwidth-help-dialog'
import { getCommitmentFields } from './bandwidth-commitment-fields'

interface BandwidthProps extends SingleProductConfigOptionProps {
    isPOC?: boolean
}

const Bandwidth: React.FC<BandwidthProps> = ({
    isPOC = false,
    index,
    name,
    productIndex,
    remove,
}) => {
    const formControl = useFormContext<Contract>()
    const { t } = useTranslation()
    const [showPriceList, setShowPriceList] = useState(true)
    const [showHelpDialog, setShowHelpDialog] = useState(false)

    const { setConfirmation } = useConfirmation()

    // Watch form values
    const configoptions = useWatch({
        control: formControl.control,
        name: `${name}` as Path<Contract>,
    })

    const product_form = useWatch({
        control: formControl.control,
        name: `order.products.[${productIndex}].product_form` as Path<Contract>,
    }) as string

    const config_name = useWatch({
        control: formControl.control,
        name: `${name}.[${index}].name` as Path<Contract>,
    }) as string

    const commitmentValue =
        useWatch({
            control: formControl.control,
            name: `${name}.[${index}].commitment_value` as Path<Contract>,
        }) || 0

    // Hooks for price calculations
    useCommitmentPriceTotal({
        control: formControl.control,
        name,
        index,
        setValue: formControl.setValue,
    })

    useCommitmentTime({
        name,
        control: formControl.control,
        index,
        setValue: formControl.setValue,
    })

    // Reset prepaid when switching to not_commit
    useEffect(() => {
        if (product_form === 'not_commit') {
            formControl.setValue(
                `${name}.[${index}].commitment_prepaid` as Path<Contract>,
                false
            )
        }
    }, [product_form])

    const handleRemove = (idx: number, configName: string) => {
        setConfirmation({
            title: t('remove-config', {
                replace: { config_name: configName },
            }),
            open: true,
            onConfirm: () => remove(idx),
        })
    }

    // Get commitment fields configuration
    const commitmentFields = getCommitmentFields(product_form, {
        name,
        index,
        isPOC,
        t,
        onShowHelp: () => setShowHelpDialog(true),
    })

    return (
        <div
            className="bg-white rounded border border-slate-200 hover:border-slate-300 transition-colors duration-200 mb-3"
            data-tour="config-bandwidth"
        >
            {/* Header */}
            <div className="px-3 py-2.5 bg-slate-50/50 border-b border-slate-200/60">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                            <ConfigurationIcons className="w-3.5 h-3.5 text-slate-600" />
                        </div>
                        <div className="min-w-0 flex-1">
                            <Typogaphy className="font-medium text-slate-800 text-sm truncate uppercase">
                                {config_name === 'global' &&
                                configoptions?.length === 1
                                    ? t('total-bandwidth')
                                    : t(config_name)}
                            </Typogaphy>
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {config_name !== 'local' && (
                            <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleRemove(index, config_name)}
                                className="hover:bg-red-50 ml-1"
                            >
                                <DeleteOutlineIcons className="w-4 h-4" />
                            </IconButton>
                        )}
                    </div>
                </div>
            </div>

            {/* Configuration Fields */}
            <div className="px-4 pt-4">
                <GridHookForm
                    subForm
                    formControl={formControl}
                    className="grid-cols-4"
                    data={[
                        // @ts-ignore
                        ...commitmentFields,
                        {
                            renderEditComponent: (control: any) => (
                                <div data-tour="bandwidth-price-method">
                                    <FormField
                                        control={control}
                                        name={`${name}.[${index}].price_config.method`}
                                        type="select"
                                        label={t('form-of-comparison')}
                                        placeholder={t('form-of-comparison')}
                                        options={PRICE_CONFIG(t)}
                                        required
                                    />
                                </div>
                            ),
                            className: isPOC ? 'hidden' : 'col-span-4',
                        },
                    ]}
                    actions={[]}
                />
            </div>

            {/* Price List Section */}
            {!isPOC && (
                <div className="border-t border-slate-200/60">
                    <div className="flex items-center justify-between px-3 py-2.5 bg-indigo-50/50 border-b border-indigo-200/60">
                        <div className="flex items-center gap-2.5">
                            <div className="w-6 h-6 bg-indigo-100 rounded flex items-center justify-center flex-shrink-0">
                                <PricingIcons className="w-3.5 h-3.5 text-indigo-600" />
                            </div>
                            <Typogaphy className="font-medium text-indigo-800 text-sm">
                                {t('contract.pricing_configuration')}
                            </Typogaphy>
                        </div>
                        <IconButton
                            color="primary"
                            size="small"
                            onClick={() => setShowPriceList(!showPriceList)}
                            className="hover:bg-green-50"
                        >
                            {showPriceList ? (
                                <EyeOffIcons className="w-4 h-4" />
                            ) : (
                                <EyeIcons className="w-4 h-4 text-primary" />
                            )}
                        </IconButton>
                    </div>
                    {showPriceList && (
                        <div className="px-3 py-2.5">
                            <PricingSection
                                name={name}
                                index={index}
                                formControl={formControl}
                                commitmentValue={commitmentValue}
                                forcePricingType="range"
                            />
                        </div>
                    )}
                </div>
            )}

            {/* Help Dialog */}
            <BandwidthHelpDialog
                open={showHelpDialog}
                onOpenChange={setShowHelpDialog}
            />
        </div>
    )
}

export default Bandwidth
